import { postOrgVendorIntegrationsByIntegrationIdExecutionByExecutionIdRevealMutation } from '@onefootprint/fetch-dashboard';
import { IcoDotSmall16 } from '@onefootprint/icons';
import type { TraceIntegrationExecutionLog } from '@onefootprint/request-types/dashboard';
import { Badge, CodeBlock, LinkButton } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePermissions from '@/hooks/use-permissions';
type IntegrationLogTimelineItemProps = {
  log: TraceIntegrationExecutionLog;
  isLast: boolean;
  defaultShowDetails?: boolean;
};
const IntegrationLogTimelineItem = ({
  log,
  isLast,
  defaultShowDetails = false
}: IntegrationLogTimelineItemProps) => {
  const {
    t
  } = useTranslation('vault-proxy', {
    keyPrefix: 'details.logs'
  });
  const [isShowingDetails, setIsShowingDetails] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const {
    hasPermission
  } = usePermissions();
  const revealMutation = useMutation(postOrgVendorIntegrationsByIntegrationIdExecutionByExecutionIdRevealMutation());
  const result = revealMutation.data as {
    isSuccess: boolean;
    isPending: boolean;
    createdAt?: string;
    output?: string;
  } | undefined;
  useEffect(() => {
    if (defaultShowDetails && !isShowingDetails && !revealMutation.data) {
      handleShowDetails();
    }
  }, [defaultShowDetails]);
  const handleShowDetails = async () => {
    if (!hasPermission('manage_vault_proxy')) {
      setIsShowingDetails(true);
      return;
    }
    try {
      await revealMutation.mutateAsync({
        path: {
          integrationId: log.integrationId,
          executionId: log.executionId
        }
      });
      setIsShowingDetails(true);
    } catch (_error) {
      setIsShowingDetails(true);
    }
  };
  const handleHideDetails = () => setIsShowingDetails(false);
  const statusVariant = result?.isPending ? 'info' : result?.isSuccess ? 'success' : 'error';
  const statusLabel = result?.isPending ? '…' : result?.isSuccess ? 'OK' : 'ERR';
  const timestamp = result?.createdAt ? new Date(result.createdAt) : null;
  return <div className="relative py-2 last:pb-0">
      {!isLast && <div className={cx('absolute top-[34px] left-[7px] w-0.5 bg-[var(--border-color-primary)]', {
      'h-[calc(100%-20px)]': isShowingDetails,
      'h-7': !isShowingDetails
    })} />}
      <div className="relative flex items-center min-h-[32px]">
        <div className="w-4 h-4 flex items-center justify-center mr-4 relative">
          <IcoDotSmall16 />
        </div>
        {/* Touch has no hover, so on a phone the details link always shows and wraps under the row. */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: hover-only affordance, mirrors proxy log timeline */}
        <div className="flex-1 min-w-0 relative flex flex-wrap items-center gap-x-2 gap-y-1 md:min-w-auto md:flex-nowrap md:gap-2" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <div className="grid grid-cols-[135px_60px_1fr] gap-3 items-center relative" style={{
          zIndex: 1
        }}>
            <div className="flex items-center justify-start gap-2">
              {timestamp ? <>
                  <span className="text-snippet-2 text-primary">{format(timestamp, 'MM/dd/yy')}</span>
                  <span className="text-snippet-2 text-primary">{format(timestamp, 'h:mm a')}</span>
                </> : <span className="text-snippet-2 text-tertiary font-mono truncate">{log.executionId}</span>}
            </div>
            {result ? <Badge variant={statusVariant}>{statusLabel}</Badge> : <span />}
            <span />
          </div>
          <span className={cx('hidden text-snippet-2 text-tertiary transition-opacity md:inline', {
          'opacity-100': isHovered || isShowingDetails,
          'opacity-0': !isHovered && !isShowingDetails
        })}>
            ⋅
          </span>
          <LinkButton variant="caption-1" onClick={isShowingDetails ? handleHideDetails : handleShowDetails} className={cx('transition-opacity', {
          'opacity-100': isHovered || isShowingDetails,
          'opacity-100 md:opacity-0': !isHovered && !isShowingDetails
        })}>
            {isShowingDetails ? t($ => $.showLess) : t($_0 => $_0.moreDetails)}
          </LinkButton>
        </div>
      </div>
      {isShowingDetails && result && <div className="ml-10 mt-2 flex flex-col bg-secondary rounded-md p-3">
          {result.isPending ? <span className="text-body-2 text-tertiary">Pending…</span> : result.output ? <CodeBlock language="json" hideHeader showLineNumbers>
              {prettyOrRaw(result.output)}
            </CodeBlock> : <span className="text-body-2 text-tertiary">No output recorded.</span>}
        </div>}
      {isShowingDetails && !result && !revealMutation.isPending && <div className="ml-10 mt-2">
          <span className="text-body-2 text-tertiary">{t($_1 => $_1.noPermission)}</span>
        </div>}
    </div>;
};
const prettyOrRaw = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};
export default IntegrationLogTimelineItem;